"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/lang";
import { Logo } from "@/components/logo";

export default function PrivacyPage() {
  const { T, lang } = useLang();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-4 px-6 py-4">
          <Link href="/" className="flex items-center gap-2 text-neutral-500 hover:text-cyan-600 transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm">{lang === "tr" ? "Ana Sayfa" : "Home"}</span>
          </Link>
          <div className="ml-auto">
            <Logo size="sm" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-neutral-400 mb-8">Last updated: April 7, 2026</p>

          <div className="prose prose-neutral max-w-none text-neutral-600 space-y-6 text-[15px] leading-relaxed">

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">1. Introduction</h2>
              <p>
                This Privacy Policy describes how CreateNano LLC (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), a limited liability
                company organized under the laws of the State of New Mexico, USA, collects, uses, discloses, and
                protects your personal information when you use the SpeakRoar platform (&quot;Platform&quot;), including our
                website, applications, and AI-powered voice generation and text-to-speech services.
              </p>
              <p>
                By accessing or using the Platform, you acknowledge that you have read, understood, and agree to
                the practices described in this Privacy Policy. If you do not agree with this policy, please do
                not use the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">2. Information We Collect</h2>
              <p>We may collect the following categories of personal information:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Account Information:</strong> Name, username, email address, encrypted password, and account creation date</li>
                <li><strong>Payment Information:</strong> Transaction records and billing details (credit card information is processed by our third-party payment processors and is not stored directly on our servers)</li>
                <li><strong>Usage Data:</strong> Voice generation history, credit usage, session logs, and feature interactions</li>
                <li><strong>Device &amp; Technical Data:</strong> IP address, browser type and version, operating system, device identifiers, and referring URLs</li>
                <li><strong>Cookies &amp; Tracking Data:</strong> Session cookies, preference cookies, and analytics cookies (see our Cookie Policy for details)</li>
                <li><strong>Communications:</strong> Information you provide when contacting our support team or submitting feedback</li>
                <li><strong>User Content:</strong> Text inputs submitted for voice generation processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">3. How We Use Your Information</h2>
              <p>We use the personal information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Creating, maintaining, and managing your user account</li>
                <li>Providing our AI voice generation and text-to-speech services</li>
                <li>Processing credit purchases and payment transactions</li>
                <li>Improving, personalizing, and optimizing the Platform and our services</li>
                <li>Responding to customer support requests and inquiries</li>
                <li>Ensuring Platform security and preventing fraud, abuse, or unauthorized access</li>
                <li>Complying with applicable legal obligations and regulatory requirements</li>
                <li>Conducting aggregated and anonymized statistical analysis and reporting</li>
                <li>Sending service-related notifications and updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">4. Sharing and Disclosure of Information</h2>
              <p>
                We do not sell your personal information. We may share your information with the following
                categories of third parties as necessary to operate the Platform:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Payment Processors:</strong> To securely process credit purchases and payment transactions</li>
                <li><strong>Cloud &amp; Hosting Providers:</strong> To host, maintain, and operate the Platform infrastructure</li>
                <li><strong>AI Service Providers:</strong> To process and deliver voice generation requests</li>
                <li><strong>Analytics Providers:</strong> To collect anonymized usage statistics and improve our services</li>
              </ul>
              <p className="mt-2">
                We may also disclose your information if required by law, regulation, legal process, or
                governmental request, or when we believe disclosure is necessary to protect our rights,
                your safety, or the safety of others, investigate fraud, or respond to an emergency.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">5. Data Security</h2>
              <p>
                We implement reasonable administrative, technical, and physical safeguards designed to protect
                your personal information from unauthorized access, disclosure, alteration, and destruction.
                These measures include encryption of data in transit and at rest, access controls, and regular
                security assessments.
              </p>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure.
                While we strive to protect your personal information, we cannot guarantee its absolute security.
                In the event of a data breach that affects your personal information, we will notify you in
                accordance with applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">6. Your Privacy Rights</h2>
              <p>
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete personal information</li>
                <li><strong>Deletion:</strong> Request that we delete your personal information, subject to certain exceptions</li>
                <li><strong>Portability:</strong> Request a portable copy of your personal information in a commonly used format</li>
                <li><strong>Opt-Out:</strong> Opt out of certain data processing activities, including marketing communications</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>.
                We will respond to your request within a reasonable timeframe, and no later than as required by applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">7. California Privacy Rights (CCPA/CPRA)</h2>
              <p>
                If you are a California resident, you have additional rights under the California Consumer
                Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA):
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Right to Know:</strong> You have the right to request that we disclose the categories and specific pieces of personal information we have collected about you, the categories of sources, the business purposes for collection, and the categories of third parties with whom we share your information.</li>
                <li><strong>Right to Delete:</strong> You have the right to request deletion of your personal information, subject to certain legal exceptions.</li>
                <li><strong>Right to Correct:</strong> You have the right to request correction of inaccurate personal information.</li>
                <li><strong>Right to Opt-Out of Sale or Sharing:</strong> We do not sell or share your personal information for cross-context behavioral advertising. If this changes, we will provide a &quot;Do Not Sell or Share My Personal Information&quot; link.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA/CPRA rights.</li>
                <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> You may request that we limit our use of sensitive personal information to only what is necessary to provide the services.</li>
              </ul>
              <p className="mt-2">
                To submit a CCPA/CPRA request, please contact us at{" "}
                <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>.
                We will verify your identity before processing your request. You may also designate an
                authorized agent to make a request on your behalf. For more details, see our{" "}
                <Link href="/ccpa" className="text-cyan-600 hover:underline">CCPA Notice</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">8. Cookies and Tracking Technologies</h2>
              <p>
                The Platform uses cookies and similar tracking technologies to enhance your experience,
                analyze usage, and support our services. For detailed information about the types of cookies
                we use, how they work, and how you can manage your preferences, please visit our{" "}
                <Link href="/cookies" className="text-cyan-600 hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">9. Children&apos;s Privacy (COPPA)</h2>
              <p>
                The Platform is not directed to children under the age of 13, and we do not knowingly collect
                personal information from children under 13. If we become aware that we have inadvertently
                collected personal information from a child under 13, we will take steps to delete such
                information as soon as possible. If you believe that we may have collected information from
                a child under 13, please contact us immediately at{" "}
                <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">10. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes for which
                it was collected, including to provide our services, comply with legal obligations, resolve
                disputes, and enforce our agreements. When you delete your account, we will delete or anonymize
                your personal information within a reasonable timeframe, except where retention is required by
                applicable law or legitimate business purposes such as fraud prevention or financial record-keeping.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">11. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than the country in
                which you reside. These countries may have data protection laws that differ from those in your
                jurisdiction. By using the Platform, you consent to the transfer of your information to the
                United States and other countries where we or our service providers operate. We take appropriate
                measures to ensure that your personal information remains protected in accordance with this
                Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices,
                technologies, legal requirements, or other factors. When we make material changes, we will
                update the &quot;Last updated&quot; date at the top of this page and, where appropriate, notify you via
                email or a prominent notice on the Platform. Your continued use of the Platform after any
                changes constitutes your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-neutral-900 mt-8 mb-3">13. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                practices, please contact us at:
              </p>
              <div className="mt-3 bg-neutral-50 rounded-lg p-4 text-sm">
                <p><strong>CreateNano LLC</strong></p>
                <p>1209 Mountain Road Pl NE, Ste N<br />Albuquerque, NM 87110, USA</p>
                <p>Phone: <a href="tel:+15054052773" className="text-cyan-600 hover:underline">(505) 405-2773</a></p>
                <p>
                  Email:{" "}
                  <a href="mailto:info@speakroar.com" className="text-cyan-600 hover:underline">info@speakroar.com</a>
                </p>
              </div>
              <p className="mt-3">
                We will endeavor to respond to all inquiries within 30 days of receipt.
              </p>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm text-neutral-400">
          <Link href="/terms" className="hover:text-cyan-600 transition-colors">Terms of Service</Link>
          <span>·</span>
          <Link href="/cookies" className="hover:text-cyan-600 transition-colors">Cookie Policy</Link>
          <span>·</span>
          <Link href="/ccpa" className="hover:text-cyan-600 transition-colors">CCPA Notice</Link>
        </div>
      </div>
    </div>
  );
}
